import { useDisclosure } from '@mantine/hooks';
import { ReactNode } from 'react';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { PageLayout } from '../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import type { StrategyLevelConfig } from './strategy-config.type';
import { StrategyHeader, StrategyTabsList } from './strategy-header';
import { StrategyTab } from './strategy.type';

type Props<TItem = any, TKPIData = any> = {
  config: StrategyLevelConfig<TItem, TKPIData>;
  children?: ReactNode;
  customShowSideSection?: boolean;
  customFilterShow?: boolean;
  customSetFilterShow?: (show: boolean) => void;
};

/**
 * Unified strategy view component that can be configured for different strategy levels
 * (Goals, Outcomes, or Initiatives)
 */
export function StrategyView<TItem = any, TKPIData = any>({
  config,
  children,
  customShowSideSection,
  customFilterShow,
  customSetFilterShow,
}: Props<TItem, TKPIData>) {
  useDocumentTitle(config.labels.pageTitle);

  const { items, query: itemsQuery } = config.hooks.useItems();
  const kpiDataHook = config.hooks.useKPIData || (() => ({ data: null, query: null as any }));
  const kpiData = kpiDataHook();

  const [filtersOpen, filtersCtrl] = useDisclosure(true);

  const isLoading = itemsQuery.isFetching || (kpiData.query && kpiData.query.isFetching);

  // Always show overview for consistency
  const isOverviewCollapsible = items.length > 0;

  // Use custom filter show/setShow if provided, otherwise use internal state
  const finalFilterShow = customFilterShow !== undefined ? customFilterShow : filtersOpen;
  const finalSetFilterShow =
    customSetFilterShow || ((show: boolean) => (show ? filtersCtrl.open() : filtersCtrl.close()));

  // Use custom showSideSection if provided, otherwise default to false
  const finalShowSideSection = customShowSideSection !== undefined ? customShowSideSection : false;

  return (
    <MenuDrivenContainer header={<StrategyHeader />} actions={config.renderers?.renderAddButton?.()}>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" transitionDuration={30} />
      <PageLayout
        controlBar={<StrategyTabsList active={config.tabId as StrategyTab} />}
        overview={{
          title: config.labels.overviewTitle,
          content: <config.components.Overview />,
          collapsible: isOverviewCollapsible,
          isEmpty: false,
          emptyLabel: config.labels.emptyLabel,
        }}
        sideFilters={
          config.features.hasFilters && config.components.Filters
            ? {
                title: config.labels.filtersTitle,
                content: <config.components.Filters setShow={finalSetFilterShow} title={config.labels.filtersTitle} />,
                show: finalFilterShow,
                setShow: finalSetFilterShow,
              }
            : undefined
        }
        mainSection={<config.components.MainSection isLoading={isLoading} />}
        sideSection={config.components.SideSection ? <config.components.SideSection /> : null}
        showSideSection={finalShowSideSection}
        data-page-layout
      />
      {children}
    </MenuDrivenContainer>
  );
}
